import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageHeader, Panel } from "@/components/Layout";
import { Switch } from "@/components/ui/switch";
import { SecurityPanel } from "@/components/SecurityPanel";
import { api, formatError, fmtDate } from "@/lib/api";

const STATUS = { pending: "text-amber-700 bg-amber-50 border-amber-200", completed: "text-emerald-700 bg-emerald-50 border-emerald-200", rejected: "text-red-700 bg-red-50 border-red-200" };
export const RequestStatus = ({ s, testId }) => <span data-testid={testId} className={`px-2 py-0.5 rounded-md border text-xs capitalize ${STATUS[s]}`}>{s}</span>;

const ExportPanel = () => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/account/export", { responseType: "blob" });
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: "postalcloud-my-data.json" });
      a.click();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Download a copy of your data">
      <p className="text-sm text-slate-500 mb-4">Instantly download your profile, letters you've sent and received, payments, requests and your audit trail activity as a JSON file. Letter PDFs can be downloaded from each letter's page.</p>
      <button data-testid="data-export-btn" onClick={download} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-sm font-semibold hover:bg-emerald-600 transition-colors">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download my data
      </button>
    </Panel>
  );
};

const RequestForm = ({ onDone }) => {
  const [type, setType] = useState("access");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/account/data-requests", { type, details });
      toast.success("Request submitted — we'll respond within one month");
      setDetails("");
      onDone();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Make a formal data request">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          {[["access", "Request a copy (subject access)"], ["erasure", "Request deletion (erasure)"]].map(([k, l]) => (
            <button type="button" key={k} data-testid={`data-request-type-${k}`} onClick={() => setType(k)}
              className={`px-4 py-3 rounded-lg border text-sm text-left transition-colors ${type === k ? "border-emerald-600 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" : "border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>{l}</button>
          ))}
        </div>
        {type === "erasure" && <p data-testid="erasure-notice" className="text-xs text-slate-500">Some records, such as payment records and the tamper-evident audit trail, may need to be kept or restricted rather than deleted. See our <Link to="/gdpr" className="underline">GDPR page</Link>.</p>}
        <div className="space-y-2">
          <Label htmlFor="dr-details">Details (optional)</Label>
          <Textarea id="dr-details" data-testid="data-request-details" value={details} onChange={(e) => setDetails(e.target.value)} maxLength={2000} rows={3} />
        </div>
        <button data-testid="data-request-submit" type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Submit request
        </button>
      </form>
    </Panel>
  );
};

const DigestPanel = () => {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/account/digest").then((r) => setOn(r.data.enabled)); }, []);
  const change = async (v) => {
    try {
      setOn((await api.patch("/account/digest", { enabled: v })).data.enabled);
      toast.success(v ? "Weekly summary turned on" : "Weekly summary turned off");
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  if (on === null) return null;
  return (
    <Panel title="Weekly delivery summary" testId="digest-panel">
      <label className="flex items-start gap-3 text-sm">
        <Switch data-testid="digest-enabled-switch" checked={on} onCheckedChange={change} />
        <span>Every Monday, email me a short list of my letters that need follow-up: delivery problems, or claim windows ending within 3 days. It's only sent when something needs attention.</span>
      </label>
    </Panel>
  );
};

export default function YourData() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/account/data-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="your-data-page" className="max-w-4xl space-y-6">
      <PageHeader eyebrow="Privacy" title="Your data" />
      <ExportPanel />
      <SecurityPanel />
      <DigestPanel />
      <RequestForm onDone={load} />
      <Panel title="Your requests">
        {!rows.length && <p data-testid="data-requests-empty" className="text-sm text-slate-500">No requests yet.</p>}
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`data-request-${r.id.slice(0, 8)}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-3 text-sm">
              <div>
                <p className="font-medium">{r.type === "access" ? "Copy of my data" : "Deletion of my data"} <span className="font-mono text-xs text-slate-400">#{r.id.slice(0, 8).toUpperCase()}</span></p>
                <p className="text-xs text-slate-500">Submitted {fmtDate(r.created_at)} · due {fmtDate(r.due_at)}{r.resolution_note ? ` · ${r.resolution_note}` : ""}</p>
              </div>
              <RequestStatus s={r.status} testId={`data-request-status-${r.id.slice(0, 8)}`} />
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
